/**
 * NEEMPOP Cart JS — AJAX Cart Drawer & Cart Engine
 */
class NeempopCart {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.backdrop = document.querySelector('[data-cart-drawer-backdrop]');
    this.closeBtn = document.querySelector('[data-cart-drawer-close]');
    this.triggers = document.querySelectorAll('[data-cart-trigger]');
    this.countBadges = document.querySelectorAll('[data-cart-count]');
    this.container = document.querySelector('[data-cart-drawer-items]');
    this.subtotalEl = document.querySelector('[data-cart-subtotal]');
    this.liveRegion = document.getElementById('cart-live-region');

    this.init();
  }

  init() {
    // Open/Close listeners
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer();
      });
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeDrawer());
    }
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.closeDrawer());
    }

    // Delegate Form submit listeners for Add-To-Cart
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (form) {
        e.preventDefault();
        this.addToCart(form);
      }
    });

    // Delegate Cart Drawer Item quantity changes & removals
    if (this.container) {
      this.container.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-cart-qty-change]');
        const removeBtn = e.target.closest('[data-cart-item-remove]');

        if (qtyBtn) {
          const key = qtyBtn.getAttribute('data-key');
          const delta = parseInt(qtyBtn.getAttribute('data-cart-qty-change'), 10);
          const input = this.container.querySelector(`input[data-key="${key}"]`);
          if (input) {
            const newQty = Math.max(0, parseInt(input.value, 10) + delta);
            this.updateQuantity(key, newQty);
          }
        } else if (removeBtn) {
          const key = removeBtn.getAttribute('data-key');
          this.updateQuantity(key, 0);
        }
      });

      this.container.addEventListener('change', (e) => {
        const input = e.target.closest('input[data-cart-qty-input]');
        if (input) {
          const key = input.getAttribute('data-key');
          const newQty = Math.max(0, parseInt(input.value, 10) || 0);
          this.updateQuantity(key, newQty);
        }
      });
    }

    // Refresh cart state on load
    this.refreshCart();
  }

  openDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-active');
    if (this.backdrop) this.backdrop.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-active');
    if (this.backdrop) this.backdrop.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  async addToCart(form) {
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn ? submitBtn.innerText : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerText = 'ADDING...';
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.description || 'Could not add item to cart');
      }

      const item = await res.json();
      this.announceLive(`${item.title} added to cart`);
      await this.refreshCart();
      this.openDrawer();
    } catch (err) {
      alert(err.message || 'Error adding to cart. Please try again.');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerText = originalText;
      }
    }
  }

  async updateQuantity(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: key, quantity: quantity })
      });

      if (!res.ok) throw new Error('Could not update cart quantity');

      const cart = await res.json();
      this.announceLive(`Cart updated. Total item count: ${cart.item_count}`);
      this.renderCart(cart);
    } catch (err) {
      console.error(err);
      alert('Cart update failed. Please refresh.');
    }
  }

  async refreshCart() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Error fetching cart:', err);
    }
  }

  renderCart(cart) {
    // Update Count Badges
    this.countBadges.forEach(badge => {
      badge.textContent = cart.item_count;
      badge.style.display = cart.item_count > 0 ? 'inline-flex' : 'none';
    });

    // Update Subtotal
    if (this.subtotalEl) {
      this.subtotalEl.textContent = this.formatMoney(cart.total_price);
    }

    // Render Line Items in Drawer if container exists
    if (!this.container) return;

    if (cart.item_count === 0) {
      this.container.innerHTML = `
        <div class="cart-drawer__empty" style="text-align: center; padding: 3rem 1rem;">
          <p style="color: var(--np-muted); margin-bottom: 1.5rem;">Your cart is currently empty.</p>
          <a href="/collections/all" class="btn btn-primary">DISCOVER NEEMPOP</a>
        </div>
      `;
      return;
    }

    let itemsHtml = '';
    cart.items.forEach(item => {
      itemsHtml += `
        <div class="cart-line-item" style="display: flex; gap: 1rem; padding: 1rem 0; border-bottom: 1px solid var(--np-border-subtle);">
          <img src="${item.image}" alt="${this.escapeHtml(item.title)}" style="width: 70px; height: 70px; object-fit: cover; border-radius: var(--np-radius-sm);">
          <div style="flex-grow: 1;">
            <a href="${item.url}" style="color: var(--np-text); font-weight: 700; text-decoration: none; font-size: 0.95rem;">${this.escapeHtml(item.product_title)}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div style="font-size: 0.8rem; color: var(--np-muted);">${this.escapeHtml(item.variant_title)}</div>` : ''}
            <div style="margin-top: 0.5rem; font-weight: 700; color: var(--np-accent);">${this.formatMoney(item.final_line_price)}</div>
            <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 0.5rem;">
              <div style="display: inline-flex; align-items: center; border: 1px solid var(--np-border-subtle); border-radius: var(--np-radius-full);">
                <button type="button" data-cart-qty-change="-1" data-key="${item.key}" aria-label="Decrease quantity" style="background: none; border: none; color: var(--np-text); padding: 0.25rem 0.6rem; cursor: pointer;">-</button>
                <input type="number" data-cart-qty-input data-key="${item.key}" value="${item.quantity}" min="0" aria-label="Quantity" style="width: 35px; text-align: center; background: none; border: none; color: var(--np-text); font-weight: 700; -moz-appearance: textfield;">
                <button type="button" data-cart-qty-change="1" data-key="${item.key}" aria-label="Increase quantity" style="background: none; border: none; color: var(--np-text); padding: 0.25rem 0.6rem; cursor: pointer;">+</button>
              </div>
              <button type="button" data-cart-item-remove data-key="${item.key}" aria-label="Remove item" style="background: none; border: none; color: var(--np-muted); font-size: 0.8rem; cursor: pointer; text-decoration: underline;">Remove</button>
            </div>
          </div>
        </div>
      `;
    });

    this.container.innerHTML = itemsHtml;
  }

  formatMoney(cents) {
    // Standard Shopify INR formatting fallback
    const rupees = (cents / 100).toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0
    });
    return `₹${rupees}`;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.innerText = str;
    return div.innerHTML;
  }

  announceLive(message) {
    if (this.liveRegion) {
      this.liveRegion.textContent = message;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.neempopCart = new NeempopCart();
});
